import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2, ArrowLeft, CalendarRange, FileBarChart2 } from "lucide-react";
import { format, startOfMonth, endOfMonth, parseISO } from "date-fns";
import { formatTime, tzForCountry, tzShort } from "@/lib/timezone";

/**
 * Employee detail dashboard. Shown when clicking an employee row on
 * /admin/employees. Isolates the "single-employee advanced stats" formerly
 * embedded inside Monthly View — dedicated space, better focus.
 *
 * Averages are computed against the TOTAL NUMBER OF WORKING DAYS (Mon–Fri) in
 * the selected range, not just days the employee actually worked. This matches
 * how HR typically reports "avg hours / working day" — an employee who is
 * absent still divides by the same denominator.
 */
export default function EmployeeDetail() {
  const { id } = useParams();
  const [user, setUser] = useState(null);
  const [countries, setCountries] = useState([]);
  const [centers, setCenters] = useState([]);
  const today = new Date();
  const [range, setRange] = useState({
    start: format(startOfMonth(today), "yyyy-MM-dd"),
    end: format(endOfMonth(today), "yyyy-MM-dd"),
  });
  const [matrix, setMatrix] = useState(null);
  const [loading, setLoading] = useState(false);

  // Load user + reference data once
  useEffect(() => {
    (async () => {
      try {
        const [u, c, ce] = await Promise.all([
          api.get("/users"),
          api.get("/countries"),
          api.get("/centers"),
        ]);
        const found = u.data.find((x) => x.id === id);
        setUser(found || false);
        setCountries(c.data);
        setCenters(ce.data);
      } catch (e) {
        setUser(false);
      }
    })();
  }, [id]);

  // Load matrix rows for the range
  useEffect(() => {
    (async () => {
      if (!user) return;
      setLoading(true);
      try {
        const params = { start: range.start, end: range.end };
        if (user.country_id) params.country_id = user.country_id;
        const { data } = await api.get("/attendance/matrix", { params });
        setMatrix(data);
      } finally {
        setLoading(false);
      }
    })();
  }, [user, range.start, range.end]);

  const activeTz = useMemo(
    () => tzForCountry(countries, user?.country_id),
    [countries, user]
  );
  const tzLabel = useMemo(() => tzShort(activeTz), [activeTz]);

  // Pick this employee out of the matrix
  const empRow = useMemo(
    () => matrix?.employees?.find((e) => e.id === id) || null,
    [matrix, id]
  );

  const stats = useMemo(() => {
    if (!empRow || !matrix) return null;

    // Working days ELAPSED so far — the denominator for daily / weekly averages.
    // We only count Mon–Fri that are on or before today; future days in the
    // range don't count. That way a mid-month view divides by the working days
    // actually reached, not the whole month.
    const todayIso = new Date().toISOString().slice(0, 10);
    let workingDaysInRange = 0;
    for (const d of matrix.days) {
      if (d > todayIso) break;                // matrix.days is sorted ascending
      const day = parseISO(d);
      const dow = day.getDay();               // 0=Sun … 6=Sat
      if (dow !== 0 && dow !== 6) workingDaysInRange += 1;
    }

    let totalHours = 0;
    let totalBreachEvents = 0;
    let daysWithBreaches = 0;
    let workedDays = 0;
    let longest = null;
    let shortest = null;

    for (const [d, cell] of Object.entries(empRow.days || {})) {
      if (cell?.status !== "Present") continue;
      const hours = Number(cell.hours || 0);
      const br = Number(cell.breaches || 0);
      totalHours += hours;
      totalBreachEvents += br;
      if (br > 0) daysWithBreaches += 1;
      if (hours > 0) {
        workedDays += 1;
        if (!longest || hours > longest.hours) longest = { date: d, hours };
        if (!shortest || hours < shortest.hours) shortest = { date: d, hours };
      }
    }

    const weeksInRange = workingDaysInRange / 5; // 5-day work week
    const dailyAvg = workingDaysInRange > 0 ? totalHours / workingDaysInRange : 0;
    const weeklyAvg = weeksInRange > 0 ? totalHours / weeksInRange : 0;
    const breachAvg = workingDaysInRange > 0 ? totalBreachEvents / workingDaysInRange : 0;

    return {
      totalHours,
      workedDays,
      workingDaysInRange,
      totalBreachEvents,
      daysWithBreaches,
      dailyAvg,
      weeklyAvg,
      breachAvg,
      longest,
      shortest,
    };
  }, [empRow, matrix]);

  const centerNames = useMemo(() => {
    if (!user?.center_ids) return "";
    return user.center_ids
      .map((cid) => centers.find((c) => c.id === cid)?.name)
      .filter(Boolean)
      .join(" · ");
  }, [user, centers]);

  const countryName = useMemo(() => {
    return countries.find((c) => c.id === user?.country_id)?.name || "—";
  }, [countries, user]);

  if (user === false) {
    return (
      <div className="p-8">
        <p className="text-red-300">Employee not found.</p>
        <Link to="/admin/employees" className="text-primary underline text-sm">Back to employees</Link>
      </div>
    );
  }
  if (!user) {
    return (
      <div className="p-8 grid place-items-center">
        <Loader2 className="animate-spin h-6 w-6 text-muted-foreground" />
      </div>
    );
  }

  const jumpThisMonth = () => {
    const now = new Date();
    setRange({
      start: format(startOfMonth(now), "yyyy-MM-dd"),
      end: format(endOfMonth(now), "yyyy-MM-dd"),
    });
  };
  const jumpLastMonth = () => {
    const now = new Date();
    const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    setRange({
      start: format(startOfMonth(prev), "yyyy-MM-dd"),
      end: format(endOfMonth(prev), "yyyy-MM-dd"),
    });
  };

  return (
    <>
      <PageHeader
        eyebrow={<Link to="/admin/employees" className="inline-flex items-center gap-1 hover:text-foreground"><ArrowLeft className="h-3 w-3" /> All employees</Link>}
        title={user.name}
        subtitle={`${user.employee_id} · ${user.email} · ${countryName} · ${centerNames || "no centers"} · times in ${tzLabel}`}
      />
      <div className="p-8 space-y-4">
        {/* ---- Date range controls ---- */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div>
            <Label className="label-mono">From</Label>
            <Input
              data-testid="detail-start"
              type="date"
              value={range.start}
              max={range.end}
              onChange={(e) => setRange((r) => ({ ...r, start: e.target.value }))}
              className="rounded-none"
            />
          </div>
          <div>
            <Label className="label-mono">To</Label>
            <Input
              data-testid="detail-end"
              type="date"
              value={range.end}
              min={range.start}
              onChange={(e) => setRange((r) => ({ ...r, end: e.target.value }))}
              className="rounded-none"
            />
          </div>
          <Button variant="outline" onClick={jumpThisMonth} className="rounded-none border-border">
            <CalendarRange className="h-4 w-4 mr-1.5" /> This month
          </Button>
          <Button variant="outline" onClick={jumpLastMonth} className="rounded-none border-border">
            Last month
          </Button>
        </div>

        {/* Detailed report CTA */}
        <div className="flex justify-end">
          <Link to={`/admin/employees/${id}/report?start=${range.start}&end=${range.end}`}>
            <Button data-testid="open-report-btn" className="rounded-none btn-primary-brand">
              <FileBarChart2 className="h-4 w-4 mr-1.5" strokeWidth={1.75} />
              Open detailed report
            </Button>
          </Link>
        </div>

        {loading && (
          <div className="py-16 grid place-items-center">
            <Loader2 className="animate-spin h-6 w-6 text-muted-foreground" />
          </div>
        )}

        {!loading && !empRow && matrix && (
          <Card className="rounded-none border-border p-6 text-center text-muted-foreground">
            No attendance data for this employee in the selected range.
          </Card>
        )}

        {!loading && stats && (
          <>
            {/* Row 1: Totals */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              <StatTile label="Total hours" value={stats.totalHours.toFixed(1) + " h"} className="text-emerald-300" />
              <StatTile label="Present days" value={empRow.summary.present} className="text-emerald-300" />
              <StatTile label="Late days" value={empRow.summary.late} className="text-amber-300" />
              <StatTile label="Absent days" value={empRow.summary.absent} className="text-red-300" />
              <StatTile label="Missing punch-out" value={empRow.summary.missing_out} className="text-red-300" />
            </div>
            {/* Row 2: Averages + Extremes */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              <StatTile
                label="Avg / working day"
                value={stats.dailyAvg.toFixed(1) + " h"}
                sub={`Over ${stats.workingDaysInRange} working day(s) elapsed`}
              />
              <StatTile
                label="Avg / week"
                value={stats.weeklyAvg.toFixed(1) + " h"}
              />
              <StatTile
                label="Longest day"
                value={stats.longest ? `${stats.longest.hours.toFixed(1)} h` : "—"}
                sub={stats.longest ? format(parseISO(stats.longest.date), "d MMM") : ""}
              />
              <StatTile
                label="Shortest day"
                value={stats.shortest ? `${stats.shortest.hours.toFixed(1)} h` : "—"}
                sub={stats.shortest ? format(parseISO(stats.shortest.date), "d MMM") : ""}
              />
              <StatTile
                label="Breaches"
                value={stats.totalBreachEvents}
                sub={`${stats.daysWithBreaches} day(s) · avg ${stats.breachAvg.toFixed(2)}/working day`}
                className={stats.totalBreachEvents ? "text-fuchsia-300" : ""}
              />
            </div>

            {/* Recent working days */}
            <Card className="rounded-none border-border p-4">
              <div className="label-mono mb-2">Day-by-day · {range.start} → {range.end}</div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left px-2 py-2 label-mono">Date</th>
                      <th className="text-left px-2 py-2 label-mono">Status</th>
                      <th className="text-left px-2 py-2 label-mono">In ({tzLabel})</th>
                      <th className="text-left px-2 py-2 label-mono">Out ({tzLabel})</th>
                      <th className="text-right px-2 py-2 label-mono">Hours</th>
                      <th className="text-right px-2 py-2 label-mono">Breaches</th>
                      <th className="text-left px-2 py-2 label-mono">Tags</th>
                    </tr>
                  </thead>
                  <tbody>
                    {matrix.days.map((d) => {
                      const cell = empRow.days[d];
                      if (!cell) return null;
                      const isFuture = cell.status === "Future";
                      return (
                        <tr key={d} className="border-b border-border/60">
                          <td className="px-2 py-1.5 font-mono-brand">{format(parseISO(d), "d MMM · EEE")}</td>
                          <td className="px-2 py-1.5">
                            <span className={
                              cell.status === "Present" ? "text-emerald-300"
                              : cell.status === "Absent" ? "text-red-300"
                              : "text-muted-foreground"
                            }>{cell.status}</span>
                          </td>
                          <td className="px-2 py-1.5 font-mono-brand">{formatTime(cell.punch_in, activeTz)}</td>
                          <td className="px-2 py-1.5 font-mono-brand">{formatTime(cell.punch_out, activeTz)}</td>
                          <td className="px-2 py-1.5 font-mono-brand text-right">{cell.hours ? cell.hours.toFixed(1) : (isFuture ? "" : "0.0")}</td>
                          <td className="px-2 py-1.5 font-mono-brand text-right">{cell.breaches || 0}</td>
                          <td className="px-2 py-1.5 text-[10px] text-muted-foreground">
                            {cell.tags?.length ? cell.tags.join(" · ") : ""}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          </>
        )}
      </div>
    </>
  );
}

function StatTile({ label, value, sub, className }) {
  return (
    <Card className="rounded-none border-border p-3">
      <div className="label-mono">{label}</div>
      <div className={`font-display font-bold text-2xl tabular-nums leading-tight ${className || ""}`}>{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground font-mono-brand mt-0.5">{sub}</div>}
    </Card>
  );
}
